/* AGEC overview — client-side 4-filter search over assets/agec-index.json.
   Progressive enhancement: the SSR Liquid-paginated list is the no-JS / crawler
   baseline; this takes over only once the visitor searches. Consumed only by
   sections/agec-overview.liquid. */

// How long to wait after the visitor stops typing before filtering.
const TYPE_DEBOUNCE_MS = 300;
(() => {
  const PAGE = 50;

  const esc = (s) =>
    String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  class AgecOverview extends HTMLElement {
    connectedCallback() {
      this.indexUrl   = this.dataset.indexUrl;
      this.detailBase = (this.dataset.detailBase || '').replace(/\/+$/, '');
      this.tplCount   = this.dataset.resultsTemplate || '[count]';
      this.totalCount = parseInt(this.dataset.total, 10) || 0;
      this.tplNone    = this.dataset.noResultsTemplate || '';
      this.moreLabel  = this.dataset.moreLabel || 'More';

      this.form     = this.querySelector('[data-agec-form]');
      this.elType   = this.querySelector('[data-agec-type]');
      this.elQ      = this.querySelector('[data-agec-q]');
      this.elCode   = this.querySelector('[data-agec-code]');
      this.elYear   = this.querySelector('[data-agec-year]');
      this.elList   = this.querySelector('[data-agec-list]');
      this.arrowSvg = this.querySelector('[data-agec-arrow]')?.innerHTML || '';
      this.elCount  = this.querySelector('[data-agec-count]');
      this.elNone   = this.querySelector('[data-agec-no-results]');
      this.elSsrPag = this.querySelector('[data-agec-ssr-pagination]');
      this.elMore   = this.querySelector('[data-agec-load-more]');

      this.items = null;      // loaded index
      this.matches = [];      // current filtered set
      this.shown = 0;         // client pagination cursor
      this.active = false;    // is a filter view active (vs SSR browse)

      this.form.addEventListener('submit', (e) => this.onSubmit(e));

      /* Type-to-filter on the Name / Code inputs in the table header. Debounced
         so it runs once the visitor stops typing rather than per keystroke —
         filtering walks the whole index client-side and syncUrl() writes history
         on each pass. These inputs sit in the <thead>, outside the filter form,
         so they need their own index warm-up. */
      /* Type / Year are the shared atomic-dropdown component over the native
         selects (assets/atomic-dropdown.js, auto-enhanced via
         [data-atomic-dropdown]). The native select stays the state carrier and
         still bubbles 'change', so listening here works for both the custom UI
         and the plain select. No debounce: a select change is a deliberate,
         single action. */
      [this.elType, this.elYear].filter(Boolean).forEach((el) => {
        el.addEventListener('change', () => this.runSearch({ skeleton: !this.items }));
      });

      [this.elQ, this.elCode].filter(Boolean).forEach((el) => {
        el.addEventListener('input', () => {
          clearTimeout(this._typeTimer);
          this._typeTimer = setTimeout(
            () => this.runSearch({ skeleton: !this.items }),
            TYPE_DEBOUNCE_MS
          );
        });
        // Warm the index on first focus so the first search is instant.
        el.addEventListener('focus', () => this.load(), { once: true });
        // Enter searches immediately; Escape clears the field (keyboard path
        // for the clear button).
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(this._typeTimer);
            this.runSearch({ skeleton: !this.items });
          } else if (e.key === 'Escape' && el.value) {
            e.preventDefault();
            this.clearInput(el);
          }
        });
      });

      // Clear (X) buttons — clear the paired input and re-filter immediately.
      this.querySelectorAll('[data-agec-clear]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const input = btn.parentElement.querySelector('.agec-overview__th-input');
          if (input) this.clearInput(input);
        });
      });

      /* Accordion: intercept a row's click and expand the detail sheet inline
         instead of navigating. Modified clicks (new tab / middle / shift) fall
         through to the real link so the standalone legal page is still
         reachable. Delegated on the list so it survives re-renders. */
      this.elList.addEventListener('click', (e) => {
        if (e.defaultPrevented || e.button === 1 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const toggle = e.target.closest('[data-agec-toggle]');
        if (!toggle || !this.elList.contains(toggle)) return;
        e.preventDefault();
        this.toggleDetail(toggle.closest('[data-agec-row]'));
      });
      this.elMore?.addEventListener('click', () => this.showMore());
      // Warm the index on first interaction so submit is instant.
      ['focus', 'pointerdown'].forEach((ev) =>
        this.form.addEventListener(ev, () => this.load(), { once: true, capture: true })
      );

      /* Progressive enhancement: with JS we page with the load-more button
         instead of the numbered pagination. The SSR pagination stays in the
         markup as the no-JS / crawler baseline and is only hidden here, once we
         know JS runs. The button shows only when there is a further page, which
         the SSR nav signals by carrying links. */
      if (this.elSsrPag && this.elMore) {
        const hasMorePages = !!this.elSsrPag.querySelector('a');
        this.elSsrPag.hidden = true;
        this.elMore.hidden = !hasMorePages;
      }

      this.hydrateFromUrl();
    }

    load() {
      if (this._loading) return this._loading;
      this._loading = fetch(this.indexUrl, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((data) => {
          this.items = data?.items || [];
          this.populateFilters();
          return this.items;
        })
        .catch(() => { this.items = []; });
      return this._loading;
    }

    populateFilters() {
      // Years + types authoritative from the index (index is source of truth).
      const years = new Set();
      const types = new Set();
      this.items.forEach((i) => { if (i.y) years.add(i.y); if (i.t) types.add(i.t); });
      // Types only — years are already rendered server-side from settings.
      if (this.elType && types.size && this.elType.options.length <= 1) {
        Array.from(types).sort().forEach((t) => {
          const o = document.createElement('option');
          o.value = t;
          o.textContent = t;
          this.elType.appendChild(o);
        });
      }
    }

    onSubmit(e) {
      e.preventDefault();
      this.runSearch({ skeleton: true });
    }

    /* Shared by the submit button and the debounced column inputs. The skeleton
       is only worth showing while the index is still being fetched — on every
       keystroke afterwards it would just flash, since filtering an
       already-loaded index is instant. */
    /* Empty a header input, re-filter now (no debounce — clearing is a
       deliberate action), and return focus to the field. The clear button hides
       itself via CSS the moment the value is gone. */
    clearInput(el) {
      if (!el.value) return;
      el.value = '';
      clearTimeout(this._typeTimer);
      this.runSearch({ skeleton: !this.items });
      el.focus();
    }

    runSearch(opts) {
      const skeleton = opts && opts.skeleton;
      this.setLoading(true);
      if (skeleton) this.showSkeleton();
      // Small floor so the skeleton doesn't flash when the index is cached.
      const floor = skeleton ? new Promise((r) => setTimeout(r, 250)) : Promise.resolve();
      return Promise.all([this.load(), floor]).then(() => {
        this.apply();
        this.syncUrl();
        this.setLoading(false);
      });
    }

    setLoading(on) { this.classList.toggle('is-loading', !!on); }

    showSkeleton(n) {
      if (this.elSsrPag) this.elSsrPag.hidden = true;
      this.elNone.hidden = true;
      this.elMore.hidden = true;
      this.elList.hidden = false;
      const row =
        '<div class="agec-overview__tr agec-overview__tr--item agec-overview__skeleton-row" aria-hidden="true">' +
          '<span class="agec-overview__td agec-overview__td--title">' +
            '<span class="agec-overview__skeleton-line agec-overview__skeleton-line--title"></span>' +
          '</span>' +
          '<span class="agec-overview__td agec-overview__td--type"><span class="agec-overview__skeleton-line"></span></span>' +
          '<span class="agec-overview__td agec-overview__td--code"><span class="agec-overview__skeleton-line"></span></span>' +
          '<span class="agec-overview__td agec-overview__td--year"><span class="agec-overview__skeleton-line"></span></span>' +
          '<span class="agec-overview__td agec-overview__td--arrow"></span>' +
        '</div>';
      this.elList.innerHTML = new Array(n || 8).fill(row).join('');
    }

    criteria() {
      return {
        type: (this.elType?.value || '').trim(),
        q:    (this.elQ?.value || '').trim().toLowerCase(),
        code: (this.elCode?.value || '').trim().toLowerCase(),
        year: (this.elYear?.value || '').trim(),
      };
    }

    apply() {
      const c = this.criteria();
      // No filter at all -> restore SSR browse view.
      if (!c.type && !c.q && !c.code && !c.year) { this.restoreBrowse(); return; }

      this.active = true;
      this.matches = this.items.filter((i) => {
        const name = (i.n || '').toLowerCase();
        const code = (i.c || '').toLowerCase();
        return (!c.type || i.t === c.type) &&
          (!c.year || i.y === c.year) &&
          // free-text box matches name OR code (forgiving)
          (!c.q    || name.includes(c.q) || code.includes(c.q)) &&
          (!c.code || code.includes(c.code));
      });

      if (this.elSsrPag) this.elSsrPag.hidden = true;
      this.elCount.textContent = this.tplCount.replace('[count]', this.matches.length);

      if (!this.matches.length) {
        this.elList.innerHTML = '';
        this.elList.hidden = true;
        this.elMore.hidden = true;
        const terms = [c.q, c.code].filter(Boolean).join(' ');
        this.elNone.hidden = false;
        this.elNone.querySelector('p').textContent = this.tplNone.replace('[terms]', terms);
        return;
      }

      this.elNone.hidden = true;
      this.elList.hidden = false;
      this.shown = 0;
      this.elList.innerHTML = '';
      this.showMore();
    }

    showMore() {
      /* Browse mode (no filter active): the SSR rows are already on screen, so
         adopt the full index and carry on *after* them instead of re-rendering
         what's there — no flash, no scroll jump. The index is only fetched at
         this point, so browsing stays cheap until someone asks for more. */
      if (!this.active) {
        this.setLoading(true);
        return this.load().then(() => {
          this.active = true;
          this.matches = this.items;
          this.shown = this.elList.querySelectorAll('.agec-overview__tr--item').length;
          this.setLoading(false);
          this.renderMore();
        });
      }
      this.renderMore();
    }

    renderMore() {
      const slice = this.matches.slice(this.shown, this.shown + PAGE);
      this.elList.insertAdjacentHTML('beforeend', slice.map((i) => this.rowHtml(i)).join(''));
      this.shown += slice.length;
      this.elMore.hidden = this.shown >= this.matches.length;
    }

    /* Must stay in step with the SSR row in sections/agec-overview.liquid — this
       replaces those rows on search, so markup drift shows up as a table that
       falls apart after the first query. */
    /* Expand one row's detail sheet, collapsing any other open one (single-open
       accordion). The sheet is fetched from the row's own detail page on first
       open and cached in the DOM thereafter. */
    toggleDetail(row) {
      if (!row) return;
      const isOpen = row.classList.contains('is-open');

      // Close whatever is currently open (including this row if it is open).
      const openRow = this.elList.querySelector('.agec-overview__row.is-open');
      if (openRow && openRow !== row) this.closeDetail(openRow);
      if (isOpen) { this.closeDetail(row); return; }

      const toggle = row.querySelector('[data-agec-toggle]');
      const detail = row.querySelector('[data-agec-detail]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      if (detail) {
        clearTimeout(row._hideTimer);
        detail.hidden = false;
        // Reflow so the 0fr height is the transition's start frame; without it
        // the change from display:none skips straight to open with no animation.
        void detail.offsetHeight;
      }
      row.classList.add('is-open');
      this.loadDetail(row);
    }

    closeDetail(row) {
      row.classList.remove('is-open');
      const toggle = row.querySelector('[data-agec-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      // Leave the fetched markup in place (cached) but hide it after the collapse
      // transition so it is not focusable/announced while closed.
      const detail = row.querySelector('[data-agec-detail]');
      if (detail) {
        clearTimeout(row._hideTimer);
        row._hideTimer = setTimeout(() => {
          if (!row.classList.contains('is-open')) detail.hidden = true;
        }, 320);
      }
    }

    /* Fetch the detail page once and lift its .agec__panels into the inline
       sheet. Kept to the panels (not the whole page) so the topbar/print/title
       chrome does not come along. Shows a loading state, and on failure falls
       back to a plain link to the full page so the content is never lost. */
    loadDetail(row) {
      if (row.dataset.loaded === '1' || row.dataset.loading === '1') return;
      const inner = row.querySelector('.agec-overview__detail-inner');
      const url = row.dataset.url;
      if (!inner || !url) return;

      row.dataset.loading = '1';
      inner.innerHTML = `<p class="agec-overview__detail-loading">${esc(this.moreLabel || '')}…</p>`;

      fetch(url, { headers: { Accept: 'text/html' } })
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const panels = doc.querySelector('.agec__panels');
          if (!panels) throw new Error('no panels');
          inner.innerHTML = '';

          /* Lift the sheet's own print button (with its localized label) into a
             top action bar — the back link and title are dropped, since inline
             you just collapse and the row already shows the name. */
          const print = doc.querySelector('.agec__print');
          if (print) {
            const actions = document.createElement('div');
            actions.className = 'agec-overview__detail-actions no-print';
            actions.appendChild(document.importNode(print, true));
            inner.appendChild(actions);
          }

          inner.appendChild(document.importNode(panels, true));

          // Footer note lives outside the panels — carry it over too.
          const footer = doc.querySelector('.agec__footer');
          if (footer) inner.appendChild(document.importNode(footer, true));

          row.dataset.loaded = '1';
        })
        .catch(() => {
          inner.innerHTML =
            '<p class="agec-overview__detail-error">' +
              `<a href="${esc(url)}">${esc(this.moreLabel || url)}</a>` +
            '</p>';
        })
        .then(() => { row.dataset.loading = ''; });
    }

    rowHtml(i) {
      const url = `${this.detailBase}/${encodeURIComponent(i.h)}`;
      const did = `agec-d-${i.h}`;
      const year = i.s || i.y;
      // Mobile meta line: repeats the columns that collapse under the title.
      let meta = '';
      if (i.t) meta += `<span>${esc(i.t)}</span>`;
      meta += `<span>${esc(i.c)}</span>`;
      meta += `<span>${esc(year)}</span>`;
      // Mirrors the SSR row in sections/agec-overview.liquid — the whole row is a
      // link (no-JS / SEO) enhanced into an accordion toggle. arrowSvg is the
      // exact same icon, read from the <template data-agec-arrow>.
      return (
        `<div class="agec-overview__row" data-agec-row data-url="${url}">` +
          `<a class="agec-overview__tr agec-overview__tr--item" href="${url}"` +
            ` data-agec-toggle aria-expanded="false" aria-controls="${did}">` +
            '<span class="agec-overview__td agec-overview__td--title">' +
              '<span class="agec-overview__td-title-wrap">' +
                `<span class="agec-overview__link">${esc(i.n)}</span>` +
                `<span class="agec-overview__td-meta">${meta}</span>` +
              '</span>' +
            '</span>' +
            `<span class="agec-overview__td agec-overview__td--type">${esc(i.t || '')}</span>` +
            `<span class="agec-overview__td agec-overview__td--code">${esc(i.c)}</span>` +
            `<span class="agec-overview__td agec-overview__td--year">${esc(year)}</span>` +
            `<span class="agec-overview__td agec-overview__td--arrow" aria-hidden="true">${this.arrowSvg}</span>` +
          '</a>' +
          `<div class="agec-overview__detail" id="${did}" data-agec-detail hidden>` +
            '<div class="agec-overview__detail-inner"></div>' +
          '</div>' +
        '</div>'
      );
    }

    /* Back to the full browse view once every filter is cleared. Restores in
       place rather than reloading the page: we only reach here after a search,
       so the index is already loaded — re-render the first page from it and
       reset the count to the full total. renderMore() draws just one page
       (PAGE rows), so this never re-renders all 11.5k. */
    restoreBrowse() {
      this.active = false;
      clearTimeout(this._typeTimer);

      const total = this.items ? this.items.length : this.totalCount;
      this.elCount.textContent = this.tplCount.replace('[count]', total);

      this.elNone.hidden = true;
      this.elList.hidden = false;

      if (this.items) {
        this.matches = this.items;
        this.shown = 0;
        this.elList.innerHTML = '';
        this.renderMore();
      }
    }

    syncUrl() {
      const c = this.criteria();
      const p = new URLSearchParams();
      if (c.type) p.set('type', c.type);
      if (this.elQ.value.trim()) p.set('q', this.elQ.value.trim());
      if (this.elCode.value.trim()) p.set('code', this.elCode.value.trim());
      if (c.year) p.set('year', c.year);
      const qs = p.toString();
      history.replaceState(null, '', qs ? `?${qs}` : location.pathname);
      this.syncedOnce = true;
    }

    hydrateFromUrl() {
      const p = new URLSearchParams(location.search);
      if (![...p.keys()].some((k) => ['type', 'q', 'code', 'year'].includes(k))) return;
      if (this.elQ)    this.elQ.value    = p.get('q')    || '';
      if (this.elCode) this.elCode.value = p.get('code') || '';
      // selects set after index loads (options may be dynamic)
      this.setLoading(true);
      this.showSkeleton();
      this.load().then(() => {
        if (this.elType && p.get('type')) this.elType.value = p.get('type');
        if (this.elYear && p.get('year')) this.elYear.value = p.get('year');
        this.apply();
        this.setLoading(false);
      });
    }
  }

  if (!customElements.get('agec-overview')) customElements.define('agec-overview', AgecOverview);
})();
